import { Transition } from "@headlessui/react";
import * as Sentry from "@sentry/react";
import { Alert, Spinner, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { duration, formatDate, isToday, now } from "common";
import dayjs from "dayjs";
import { groupBy, isNil, sortBy } from "lodash";
import pluralize from "pluralize";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { GroupedVirtuoso } from "react-virtuoso";
import { Experience, Timeslot } from "types";
import shallow from "zustand/shallow";
import { queryClient } from "../../api/queryClient";
import { useScrollToTimeslotList } from "../../hooks/Dashboard/useScrollToTimeslot";
import { useInfiniteCollection } from "../../hooks/useInfiniteCollection";
import useTimeslot from "../../hooks/useTimeslot";
import { useDashboardFilterStore } from "../../stores/dashboardFilterStore";
import { DashboardScope } from "../../types/Dashboard";
import { createQueryString } from "../../utils/createQueryString";
import { SecondaryButton } from "../Common/SecondaryButton";
import { InteracRefundWarningModal } from "../PurchaseModification/Refund/InteracRefundWarningModal";
import { getTimeslotParams } from "./Common/timeslotParams";
import { DashboardListSkeleton } from "./DashboardListSkeleton";
import { DashboardSummaryHeader } from "./DashboardSummaryHeader";
import { useDashboardTableStore } from "./DashboardTable/useDashboardTableStore";
import { DashboardItem } from "./Item/DashboardItem";
import { DashboardRoster } from "./Roster/DashboardRoster";
import { useCaptureQueryError } from "@/hooks/useCaptureQueryError";
import { useInteracPurchaseRefundStore } from "@/stores/interacPurchaseRefundStore";

interface DashboardListProps {
    height: number;
}

// Maximum number of list elements before GroupedVirtuoso is rendered
const DASHBOARD_ITEMS_WITHOUT_VIRTUOSO = 80;

export const DashboardList = ({ height }: DashboardListProps) => {
    const history = useHistory();
    const urlParams = useParams<{ timeslotId?: string }>();
    const [previousGroupCount, setPreviousGroupCount] = useState(0);
    const [showRoster, setShowRoster] = useState(false);

    const { start, setStart, setEndDate, filters, isRealTimeUpdate } = useDashboardFilterStore((state) => {
        return {
            start: state.date,
            setStart: state.setDate,
            setEndDate: state.setEndDate,
            filters: state.filters,
            isRealTimeUpdate: state.isRealTimeUpdate,
        };
    }, shallow);

    const [isInteracRefundModalOpen, interacPurchaseDetails, setInteracRefundModalClose] =
        useInteracPurchaseRefundStore((state) => [state.isOpen, state.interacPurchaseDetails, state.setClose], shallow);

    // Number of days to fetch from the server
    const range = filters.scope === DashboardScope.ALL ? 0 : 2;
    const getParams = getTimeslotParams();
    const end = useMemo(() => start.add(range, "days"), [start, range]); // Using range instead of offsetStart.current to prevent race conditions as useMemo is executed before useEffect
    const params = useMemo(() => getParams(start, end), [getParams, start, end]);

    // we cannot use useResource for timeslots, since it will make call to /timeslots/:id incase it is unable to
    // find the timeslot in the cache and it will return 404 leading to showing error flash messages.
    const selectedTimeslot = useTimeslot(urlParams?.timeslotId);

    const {
        data: timeslots,
        isLoading: isLoadingTimeslots,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
        isFetching,
        error: timeslotError,
    } = useInfiniteCollection<Timeslot>("timeslots", params, {
        // Only fetch timeslots once we have a valid date with the loaded seller's timezone, and,
        // when we have fetched plugins from elrond so that we know what params to use for timeslot based on feature flags
        enabled: start !== null,
        keepPreviousData: true,
        refetchOnMount: "always", // Refetch when the component mounts
        staleTime: duration(1, "seconds"), // Don't re-fetch if the params have changed within this period. After that it will show stale data and re-fetch in the background
        // See explanation for use of the below two props https://xola01.atlassian.net/browse/X2-5386?focusedCommentId=102705
        notifyOnChangeProps: ["data", "error", "isFetching", "isFetchingNextPage", "isLoading"], // trigger render when data changed (or on error).
        notifyOnChangePropsExclusions: ["isStale"], // don't trigger render when cache become stale.
    });

    useCaptureQueryError(timeslotError, {
        tags: { component: "DashboardList", action: "fetchTimeslots" },
        extra: { start, params },
    });

    // Offset start date, for loading more events with "Load More" button
    const offsetStart = useRef(range);

    // Reset offsetStart when date changes
    useEffect(() => {
        offsetStart.current = range;
        setPreviousGroupCount(0);
        setEndDate(end);
    }, [start, end, filters, range, setEndDate, setPreviousGroupCount]);

    const isCollapsed = !!selectedTimeslot;
    const isFetchedTimeslot = timeslots?.some((timeslot) => timeslot.id === urlParams.timeslotId);
    const isLoading = isLoadingTimeslots || (isFetching && !isFetchingNextPage);

    useEffect(() => {
        useDashboardTableStore.setState({ isLoading });
    }, [isLoading]);

    // change start date if there is timeslotId in url and timeslot is not in response
    useEffect(() => {
        if (urlParams.timeslotId && !isFetchedTimeslot) {
            const [, startDateFromUrl] = urlParams.timeslotId.split("_");
            setStart(now(startDateFromUrl));
        }
    }, [urlParams.timeslotId, selectedTimeslot, setStart, isFetchedTimeslot]);

    const content = useMemo(() => getContent(timeslots, filters.scope), [timeslots, filters.scope]);

    const isBelowVirtuosoThreshold = content.items.length < DASHBOARD_ITEMS_WITHOUT_VIRTUOSO;

    const groupCounts = useMemo(() => content.groups.map((group) => group.timeslots.length), [content]);
    const groupCountsLength = groupCounts.length;

    // Reset offsetStart when dashboard items come back to initial state
    useEffect(() => {
        if (groupCountsLength >= previousGroupCount) {
            setPreviousGroupCount(groupCountsLength);
        }

        if (previousGroupCount > groupCountsLength) {
            offsetStart.current = range;
            setPreviousGroupCount(0);
        }
    }, [groupCountsLength, previousGroupCount, range]);

    const handleToggleRoster = useCallback(() => {
        history.push("/dashboard");
    }, [history]);

    const handleLoadMoreClick = () => {
        if (hasNextPage) {
            loadNextTimeslots();
        } else {
            loadNextTwoDays();
        }
    };

    const loadNextTwoDays = () => {
        // We can easily just update the end date range and re-fetch /timeslots, but that would unnecessarily be a big API call
        // So we only fetch the subset of the "Load More" range we need. Example: If at first we fetch 01-03 then on
        // load more we will fetch 04-06 instead of 01-06 again (which would be easier to code)
        const nextStart =
            filters.scope === DashboardScope.ALL
                ? start.add(offsetStart.current + 1, "days")
                : end.add(offsetStart.current - 1, "days"); // -1 required otherwise you will skip a day
        const endDate = nextStart.add(range, "days");
        setEndDate(endDate);

        const nextParams = getParams(nextStart, endDate);
        fetchNextPage({ pageParam: `/timeslots?${createQueryString(nextParams)}` });
        // Because our end date includes slots for that day, so the next start should be one day after the end date
        // See date range example above
        offsetStart.current += range + 1;
    };

    const loadNextTimeslots = () => {
        // Will load the following timeslots for the same day
        fetchNextPage();
    };

    const handleDateChange = useCallback(
        (date: Date) => {
            setStart(dayjs(date));
        },
        [setStart],
    );

    useEffect(() => {
        if (isCollapsed && showRoster) {
            setShowRoster(false);
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [isCollapsed]);

    const { virtuoso } = useScrollToTimeslotList(start, filters, timeslots, isRealTimeUpdate);

    const dashboardItemsRef = useRef<Record<string, HTMLSpanElement | null>>({});
    const hasAutoScrolledRef = useRef(false);

    // Scroll for incoming timeslot for the case when list is rendered without virtuoso.
    useEffect(() => {
        // When realtime update happens don't scroll to the next event
        if (isRealTimeUpdate || hasAutoScrolledRef.current) {
            return;
        }

        const timeslotId = isToday(start) ? getScrollToTimeslotId(timeslots) : null;
        const itemReference = timeslotId ? dashboardItemsRef.current[timeslotId] : null;

        if (itemReference) {
            itemReference.scrollIntoView({ behavior: "smooth" });
            hasAutoScrolledRef.current = true; // prevent future auto-scrolls
        }
    }, [dashboardItemsRef, start, timeslots, isRealTimeUpdate]);

    const getTimeslotFromContentItem = useCallback((index: number) => content.items[index], [content]);
    const getTimeslotsFromContentGroup = useCallback((index: number) => content.groups[index], [content]);

    const itemContent = useCallback(
        (itemIndex: number) => {
            const timeslot = getTimeslotFromContentItem(itemIndex);

            return (
                <DashboardItem
                    key={timeslot.id}
                    isVirtualized={!isBelowVirtuosoThreshold}
                    timeslot={timeslot}
                    isCollapsed={isCollapsed}
                    isSelected={selectedTimeslot?.id === timeslot?.id}
                />
            );
        },
        [getTimeslotFromContentItem, isCollapsed, selectedTimeslot, isBelowVirtuosoThreshold],
    );

    const groupContent = useCallback(
        (groupIndex: number) => {
            const groupItem = getTimeslotsFromContentGroup(groupIndex);
            return (
                <HeaderGroupContent
                    index={groupIndex}
                    groupItem={groupItem}
                    isCollapsed={isCollapsed}
                    handleToggleRoster={handleToggleRoster}
                    onChange={handleDateChange}
                />
            );
        },
        [getTimeslotsFromContentGroup, handleToggleRoster, handleDateChange, isCollapsed],
    );

    // Show a skeleton when we load the timeslots the first time
    if (!start || (isLoadingTimeslots && !selectedTimeslot)) {
        return <DashboardListSkeleton date={start} rows={4} />;
    }

    if (!timeslots) {
        Sentry.captureMessage("No timeslots found", {
            level: "error",
            tags: { component: "DashboardList", action: "render" },
            extra: { start, params },
        });
        console.error("Error fetching timeslots for", { start, offsetStart, params });

        return <Alert color="danger">An unexpected error occurred. No timeslots found.</Alert>;
    }

    const sideBar: HTMLElement | null = document.querySelector(".ui-sidebar"); // UI Kit should implement forwardRef for the sidebar
    const sideBarWidth = sideBar?.offsetWidth ?? 0;
    const maxDashboardWidth = document.documentElement.clientWidth - (sideBarWidth ?? 0) - 1; // 1 need for better responsive while window resizing

    return (
        <div className="flex h-full lg:flex-row" style={{ maxWidth: maxDashboardWidth }}>
            {isBelowVirtuosoThreshold ? (
                <div
                    className={clsx(
                        "ui-dashboard-list w-full overflow-y-auto",
                        isCollapsed && "border-r border-gray-light",
                        isCollapsed ? "w-56 flex-shrink-0 flex-col lg:w-56 xl:w-60" : "!w-full",
                    )}
                >
                    {content.groups.map((group, index) => {
                        return (
                            <React.Fragment key={group.date}>
                                <div className="sticky top-0 z-10">{groupContent(index)}</div>
                                {content.items.map((timeslot, timeslotIndex) => {
                                    return timeslot.date === group.date ? (
                                        <span
                                            key={timeslot.id}
                                            ref={(element) => {
                                                dashboardItemsRef.current[timeslot.id] = element;
                                            }}
                                            className="scroll-m-10"
                                        >
                                            {itemContent(timeslotIndex)}
                                        </span>
                                    ) : null;
                                })}
                            </React.Fragment>
                        );
                    })}

                    <ListFooter
                        isLoading={isFetchingNextPage || isLoadingTimeslots || isFetching}
                        hasNextPage={hasNextPage}
                        hasNoTimeslots={content.items.length === 0}
                        range={range}
                        onLoadMore={handleLoadMoreClick}
                    />
                </div>
            ) : (
                <div className={clsx(isCollapsed ? "w-56 flex-shrink-0 flex-col lg:w-56 xl:w-60" : "!w-full")}>
                    <GroupedVirtuoso
                        ref={virtuoso}
                        style={{ height }}
                        className={clsx("ui-dashboard-list w-full", isCollapsed && "border-r border-gray-light")}
                        groupCounts={groupCounts}
                        groupContent={groupContent}
                        itemContent={itemContent}
                        components={{
                            Footer: () => (
                                <ListFooter
                                    isLoading={isFetchingNextPage || isLoadingTimeslots || isFetching}
                                    hasNextPage={hasNextPage}
                                    hasNoTimeslots={content.items.length === 0}
                                    range={range}
                                    onLoadMore={handleLoadMoreClick}
                                />
                            ),
                        }}
                    />
                </div>
            )}
            {selectedTimeslot && (
                <Transition
                    show={isCollapsed}
                    appear={isCollapsed}
                    as="div"
                    className="flex w-full flex-col overflow-y-auto"
                    enter="transition-all duration-250"
                    enterFrom="transform translate-x-full"
                    enterTo="transform translate-x-0"
                    afterEnter={() => setTimeout(() => setShowRoster(true), 100)}
                >
                    <div className="px-5 py-3 pr-3 pb-0 xl:space-x-6 xl:px-10 xl:py-5">
                        <DashboardRoster timeslot={selectedTimeslot} />
                    </div>
                </Transition>
            )}
            {isInteracRefundModalOpen && (
                <InteracRefundWarningModal
                    isOpen={isInteracRefundModalOpen}
                    interacPurchaseDetails={interacPurchaseDetails}
                    onClose={setInteracRefundModalClose}
                />
            )}
        </div>
    );
};

interface ListFooterProps {
    isLoading: boolean;
    onLoadMore: () => void;
    hasNextPage?: boolean;
    hasNoTimeslots: boolean;
    range: number;
}

const ListFooter = ({ isLoading, onLoadMore, hasNextPage, hasNoTimeslots, range }: ListFooterProps) => {
    const tooltipContent = hasNextPage ? (
        "Load events for the following hours"
    ) : (
        <span>
            Load events for the next <var data-var="range">{range + 1} </var>
            {pluralize("day", range + 1)}
        </span>
    );
    return (
        <div className="p-4 text-center">
            {isLoading ? (
                <Spinner />
            ) : (
                <div className="flex flex-col justify-center">
                    {hasNoTimeslots && <span className="pb-4 pt-4 text-lg font-bold text-black">No results found</span>}
                    <Tooltip content={tooltipContent} offset={[0, 20]}>
                        <SecondaryButton onClick={onLoadMore}>Load More</SecondaryButton>
                    </Tooltip>
                </div>
            )}
        </div>
    );
};

const getContent = (timeslots: Timeslot[], scope: DashboardScope) => {
    // The function call is memoized so we don't need memoization here

    const filteredTimeslots =
        scope === DashboardScope.RESERVED
            ? timeslots.filter((timeslot) => timeslot.capacity.reserved !== 0)
            : timeslots;

    // Remove duplicate timeslots that can appear due to realtime updates
    const uniqTimeslots = [...new Map(filteredTimeslots.map((t) => [t.id, t])).values()];
    const sortedTimeslots = sortBy(uniqTimeslots, getSortKey);
    const timeslotsByDate = groupBy(sortedTimeslots, "date");

    const groups = Object.entries(timeslotsByDate).map(([date, timeslots]) => ({ date, timeslots }));

    return { groups, items: sortedTimeslots };
};

const getExperience = (productId: ID) => {
    return queryClient.getQueryData<Experience>(["products", productId]);
};

const getSortKey = (timeslot: Timeslot) => {
    // Get the date and time for sorting
    const dateTimeKey = timeslot.arrivalDatetime?.valueOf() ?? timeslot.date;

    // Get the timeslot name, trim leading backslashes, and split
    const nameKey = getExperience(timeslot.product)?.name.trimStart().toLowerCase(); // Trim leading backslashes

    return [dateTimeKey, nameKey];
};

interface HeaderGroupContentProps {
    index: number;
    groupItem: {
        date: string;
        timeslots: Timeslot[];
    };
    isCollapsed: boolean;
    handleToggleRoster: () => void;
    onChange: (date: Date) => void;
}

const HeaderGroupContent = ({
    index,
    groupItem,
    isCollapsed,
    handleToggleRoster,
    onChange,
}: HeaderGroupContentProps) => {
    if (!groupItem) {
        console.error("Error fetching group item", { index, groupItem });
        return null;
    }

    const { date, timeslots } = groupItem;

    if (date === "null" || isNil(date)) {
        console.error("Missing or invalid date", { date, timeslots });
        return null;
    }

    return (
        <span className={clsx("inline-block w-full", index === 0 ? "bg-blue-lighter" : "bg-gray-lighter")}>
            <DashboardSummaryHeader
                date={formatDate(date)}
                timeslots={timeslots}
                isCollapsed={isCollapsed}
                onClick={handleToggleRoster}
                onChange={onChange}
            />
        </span>
    );
};

const getScrollToTimeslotId = (timeslots: Timeslot[]) => {
    const cutOffTime = Number.parseInt(dayjs().format("HHmm"), 10);
    const today = dayjs().format("YYYY-MM-DD");

    let timeslotId: string | null = null;

    for (const slot of timeslots) {
        if (slot.date !== today) continue;

        timeslotId = slot.id; // keep updating to hold the last id

        if (slot.time && slot.time > cutOffTime) {
            timeslotId = slot.id;
            break; // return on first valid future slot
        }
    }

    return timeslotId;
};
